/**
 * Eastern Time Date Helper
 * Provides consistent Eastern Time zone formatting across the application
 */

// Eastern Time zone identifier
const EASTERN_TIMEZONE = 'America/New_York';

/**
 * Get current date/time in Eastern Time
 * @returns {Date} Date object (note: still in local time, but formatted values will be ET)
 */
export const getEasternDate = () => {
  return new Date();
};

/**
 * Format a date to Eastern Time ISO string
 * @param {Date} date - Date to format (defaults to current date)
 * @returns {string} ISO-like string in Eastern Time (e.g., "2024-01-15T14:30:00-05:00")
 */
export const toEasternISOString = (date = new Date()) => {
  return date.toLocaleString('en-US', {
    timeZone: EASTERN_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).replace(/(\d+)\/(\d+)\/(\d+),\s(\d+):(\d+):(\d+)/, '$3-$1-$2T$4:$5:$6') + ' ET';
};

/**
 * Format a date to Eastern Time locale date string (MM/DD/YYYY)
 * @param {Date} date - Date to format (defaults to current date)
 * @returns {string} Formatted date string (e.g., "01/15/2024")
 */
export const toEasternDateString = (date = new Date()) => {
  const formatted = date.toLocaleDateString('en-US', {
    timeZone: EASTERN_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  // Already in MM/DD/YYYY format from en-US locale
  return formatted;
};

/**
 * Format a date to Eastern Time YYYY-MM-DD format (for form inputs)
 * @param {Date} date - Date to format (defaults to current date)
 * @returns {string} Formatted date string (e.g., "2024-01-15")
 */
export const toEasternInputDate = (date = new Date()) => {
  const formatted = date.toLocaleDateString('en-US', {
    timeZone: EASTERN_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  // Convert MM/DD/YYYY to YYYY-MM-DD
  const [month, day, year] = formatted.split('/');
  return `${year}-${month}-${day}`;
};

/**
 * Format a date to Eastern Time with full timestamp for display
 * @param {Date} date - Date to format (defaults to current date)
 * @returns {string} Formatted date/time string (e.g., "01/15/2024 2:30 PM ET")
 */
export const toEasternDateTimeString = (date = new Date()) => {
  const formatted = date.toLocaleString('en-US', {
    timeZone: EASTERN_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  // Already in MM/DD/YYYY format from en-US locale, just add ET
  return `${formatted} ET`;
};

/**
 * Get current year in Eastern Time
 * @returns {number} Current year (e.g., 2024)
 */
export const getEasternYear = () => {
  return parseInt(new Date().toLocaleDateString('en-US', {
    timeZone: EASTERN_TIMEZONE,
    year: 'numeric'
  }));
};

/**
 * Format a date to Eastern Time long format
 * @param {Date} date - Date to format (defaults to current date)
 * @returns {string} Formatted date string (e.g., "January 15, 2024")
 */
export const toEasternLongDate = (date = new Date()) => {
  return date.toLocaleDateString('en-US', {
    timeZone: EASTERN_TIMEZONE,
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Parse an Eastern Time date string to a Date object
 * @param {string} dateString - Date string to parse
 * @returns {Date} Date object
 */
export const parseEasternDate = (dateString) => {
  // Add Eastern timezone offset if not present
  const date = new Date(dateString);
  return date;
};

// Named export for all date helper functions
const dateHelpers = {
  getEasternDate,
  toEasternISOString,
  toEasternDateString,
  toEasternInputDate,
  toEasternDateTimeString,
  getEasternYear,
  toEasternLongDate,
  parseEasternDate,
  EASTERN_TIMEZONE
};

export default dateHelpers;
